import React, {useEffect, useRef, useState} from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import notificacionService from "../../_services/cart/notification.service";
import pushService from "../../_services/cart/push.service";
import {history} from "../../_helpers";

/**
 * La campana del header: lo que pasó mientras el usuario no estaba mirando.
 *
 * <h3>Es el registro, no el push</h3>
 * Acá aparece todo, tenga o no permitidas las notificaciones del navegador. El push es un atajo para
 * enterarse afuera del sitio; si la campana dependiera de él, un usuario que nunca aceptó el permiso
 * no tendría forma de saber que le vendieron algo salvo entrando a mirar la pantalla de ventas.
 *
 * <h3>Acá también se puede activar el push</h3>
 * Es el segundo lugar donde se ofrece: el primero es el cartel de la home ({@code PushOptIn}), que
 * alcanza al que nunca abre la campana. En los dos casos el permiso lo dispara un click y no la
 * carga de la pantalla —un `requestPermission()` que el usuario no buscó es la forma más rápida de
 * que lo bloquee para siempre, y el bloqueo no se revierte desde el código.
 */
export const NotificacionesBell = () => {

    const [abierto, setAbierto] = useState(false);
    const [notificaciones, setNotificaciones] = useState([]);
    const [pushActivo, setPushActivo] = useState(pushService.isGranted);
    const [pushBloqueado, setPushBloqueado] = useState(pushService.isDenied);
    const contenedor = useRef(null);

    useEffect(() => notificacionService.onCambio(setNotificaciones), []);

    /** Cerrar al tocar afuera. Sin esto el panel se queda abierto tapando media pantalla. */
    useEffect(() => {
        if (!abierto) return undefined;

        const alTocarAfuera = (e) => {
            if (contenedor.current && !contenedor.current.contains(e.target)) setAbierto(false);
        };
        document.addEventListener("mousedown", alTocarAfuera);
        return () => document.removeEventListener("mousedown", alTocarAfuera);
    }, [abierto]);

    const sinLeer = notificaciones.filter((n) => !n.viewed).length;

    const abrirNotificacion = (n) => {
        setAbierto(false);
        notificacionService.markAsViewed(n.id);
        if (n.deepLink) {
            history.push(n.deepLink);
            window.scrollTo(0, 0);
        }
    };

    const activarPush = async () => {
        const {success, blocked} = await pushService.activar();
        setPushActivo(success);
        setPushBloqueado(blocked);
    };

    // iOS sólo deja suscribirse con el sitio agregado a la pantalla de inicio: ahí el botón no
    // serviría de nada y lo que corresponde es decir cómo instalarlo.
    const iosSinInstalar = pushService.iosSinInstalar;

    const formatear = (iso) => {
        if (!iso) return "";
        const fecha = new Date(iso);
        const minutos = Math.round((Date.now() - fecha.getTime()) / 60000);
        if (minutos < 1) return "Recién";
        if (minutos < 60) return `Hace ${minutos} min`;
        if (minutos < 60 * 24) return `Hace ${Math.round(minutos / 60)} h`;
        return fecha.toLocaleDateString("es-AR", {day: "2-digit", month: "2-digit"});
    };

    return (
        <div className="campana" ref={contenedor}>
            <button
                type="button"
                className="campana__boton"
                onClick={() => setAbierto(!abierto)}
                aria-label={sinLeer ? `Notificaciones (${sinLeer} sin leer)` : "Notificaciones"}
            >
                <FontAwesomeIcon icon="fa-regular fa-bell" size="lg"/>
                {sinLeer > 0 && <span className="campana__globo">{sinLeer > 9 ? "9+" : sinLeer}</span>}
            </button>

            {abierto && (
                <div className="campana__panel" role="dialog" aria-label="Notificaciones">
                    <div className="campana__panelHeader">
                        <strong>Notificaciones</strong>
                        {sinLeer > 0 && (
                            <button type="button" onClick={() => notificacionService.markAllAsViewed()}>
                                Marcar todas
                            </button>
                        )}
                    </div>

                    {!pushActivo && !pushBloqueado && pushService.isSupported && (
                        <button type="button" className="campana__optin" onClick={activarPush}>
                            <FontAwesomeIcon icon="fa-solid fa-bell"/>
                            <span>
                                <strong>Activar notificaciones</strong>
                                Enterate de tus ventas y mensajes aunque no tengas el sitio abierto.
                            </span>
                        </button>
                    )}

                    {iosSinInstalar && (
                        <p className="campana__bloqueado">
                            Para recibir avisos en el iPhone, agregá DeAutopartes a la pantalla de inicio
                            desde Compartir → Agregar a inicio, y abrilo desde ahí.
                        </p>
                    )}

                    {pushBloqueado && (
                        <p className="campana__bloqueado">
                            Las notificaciones están bloqueadas en este navegador. Se habilitan desde el
                            candado de la barra de direcciones.
                        </p>
                    )}

                    <div className="campana__lista">
                        {notificaciones.length === 0 && (
                            <div className="campana__vacio">
                                <FontAwesomeIcon icon="fa-regular fa-bell-slash" size="2x"/>
                                <p>No tenés notificaciones nuevas.</p>
                            </div>
                        )}

                        {notificaciones.map((n) => (
                            <button
                                key={n.id}
                                type="button"
                                className="campana__item"
                                onClick={() => abrirNotificacion(n)}
                            >
                                <span className="campana__itemIcono">
                                    <FontAwesomeIcon icon={iconoDe(n.type)}/>
                                </span>
                                <span className="campana__itemCuerpo">
                                    <span className="campana__itemTitulo">{n.title}</span>
                                    {n.body && <span className="campana__itemBody">{n.body}</span>}
                                    <span className="campana__itemFecha">{formatear(n.createdInstant)}</span>
                                </span>
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

/** Un ícono por tipo: de un vistazo se distingue una venta de un mensaje sin leer el título. */
const iconoDe = (tipo) => {
    switch (tipo) {
        case "NUEVO_MENSAJE":
            return "fa-regular fa-comment-dots";
        case "NUEVA_VENTA":
            return "fa-solid fa-hand-holding-dollar";
        case "NUEVA_COMPRA":
            return "fa-solid fa-bag-shopping";
        case "NUEVO_PAGO":
            return "fa-solid fa-credit-card";
        case "VENTA_CANCELADA":
            return "fa-solid fa-ban";
        case "NUEVO_ESTADO_COMPRA":
            return "fa-solid fa-truck-fast";
        // Sin este caso caia en el default, que es la misma campana del boton que abre la lista:
        // el aviso mas importante para un comprador quedaba siendo el unico sin icono propio.
        case "ALERTA_PRODUCTO":
            return "fa-solid fa-bullseye";
        case "COMPROBANTE_TRANSFERENCIA":
            return "fa-solid fa-receipt";
        default:
            return "fa-regular fa-bell";
    }
};
